import React, { Fragment, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useDispatch, connect } from 'react-redux';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import { ButtonBase, Collapse } from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowRight } from '@material-ui/icons';
import { PromotionValidation } from 'containers/QuickShop/Loadable';
import Loader from 'components/Loader';
import { Button, Dialog, H2, Div, Span } from 'components/Ui';
import EquipmentNotification from 'components/Dialogs/EquipmentNotification/Loadable';
import { InfoIcon } from 'images';
import { INFORMATION_PAGE } from 'containers/PrimoCheckoutPage/constants';
import { useIsMobile } from 'utils/hooks';
import { isAcquisitionDomain, getRegionalBrandName } from 'utils/domainHelper';
import {
  injectVariables,
  translateData,
  getToUrl,
  isCanadianBrand,
} from 'utils/translation';
import HowToReadYourBillDialog from 'containers/HowToReadYourBillDialog';
import DescriptionIcon from '@material-ui/icons/Description';

import { loadMerchandizedProducts } from 'containers/PrimoProducts/actions';
import { selectProductsData } from 'containers/PrimoProducts/selectors';
import {
  selectAcquisitionVariables,
  selectCostcoVariables,
} from 'containers/Landing/selectors';
import { selectSummaryLoading } from 'containers/Cart/selectors';

import { find } from 'lodash';
import Typography from '@material-ui/core/Typography';
import { formatCurrency } from 'utils/common';
import { createStructuredSelector } from 'reselect';
import styles from './styles.scss';

const PriceSummary = ({
  total,
  subtotal,
  energySurcharge,
  isEstimatedDeliveryFee,
  paymentSurcharges,
  tax,
  pstTax,
  qstTax,
  gstTax,
  hstTax,
  containerDeposit,
  containerRecyclingFee,
  otherTaxesFees,
  deposit,
  recurringCharge,
  isShowCheckoutButton = false,
  isSummaryLoading,
  promotionId,
  discount,
  products,
  cartItems,
  costcoVariables,
  acquisitionVariables,
}) => {
  const initialPaySurchargeAmt = paymentSurcharges?.initial?.surchargeAmount;
  const recurringPaySurchargeAmt =
    paymentSurcharges?.recurring?.surchargeAmount;
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [isCostco, setIsCostco] = useState(false);
  const [isShowTaxes, changeIsShowTaxes] = useState(false);
  const [isShowPromotion, changeIsShowPromotion] = useState(false);
  const toggleTaxes = () => changeIsShowTaxes(!isShowTaxes);
  const toggleShowPromotion = () => changeIsShowPromotion(!isShowPromotion);

  const [isShowDetails, changeIsShowDetails] = useState(false);
  const toggleDetails = () => {
    changeIsShowDetails(!isShowDetails);
    const seeDetails = document.querySelector('#cartTotal');
    seeDetails.setAttribute('tabindex', -1);
    seeDetails.focus();
  };

  const [isViewSampleInvoice, changeIsViewSampleInvoice] = useState(false);
  const toggleViewSampleInvoice = () =>
    changeIsViewSampleInvoice(!isViewSampleInvoice);

  const [modalStates, changeModalStates] = useState({
    surcharge: false,
    deposit: false,
    paySurcharge: false,
    taxes: false,
    pst: false,
    hst: false,
    gst: false,
    qst: false,
    cdf: false,
    crf: false,
    isDispeserMissing: false,
  });
  const toggleModal = (name) =>
    changeModalStates({ ...modalStates, [name]: !modalStates[name] });
  const toggleSurcharge = () => toggleModal('surcharge');
  const toggleGST = () => toggleModal('gst');
  const togglePST = () => toggleModal('pst');
  const toggleHST = () => toggleModal('hst');
  const toggleQST = () => toggleModal('qst');
  const toggleCRF = () => toggleModal('crf');
  const toggleCDF = () => toggleModal('cdf');
  const toggleDeposit = () => toggleModal('deposit');
  const togglePaySurcharge = () => toggleModal('paySurcharge');
  const toggleTax = () => toggleModal('taxes');
  const { dispenserMissing } = modalStates;
  const toggleDispeserMissing = () => toggleModal('dispenserMissing');

  const isAcquisition = isAcquisitionDomain();

  const waterBottle = find(cartItems, {
    category: 'Large Bottle',
  });

  const hasCartDispenser = find(cartItems, {
    category: 'Dispenser',
  });
  const equipmentItems = products.filter(
    (itemType) => itemType.category === 'Dispenser',
  );

  const isCartDispenserMissing =
    waterBottle && hasCartDispenser === undefined && promotionId === undefined;
  useEffect(() => {
    if (isCartDispenserMissing) {
      dispatch(
        loadMerchandizedProducts({ categoryId: '78CFc2wyWFCb9Kwz2t93H1' }),
      );
    }
  }, [isCartDispenserMissing]);
  const goToCheckout = () => {
    if (isCartDispenserMissing === true && isAcquisition) {
      toggleDispeserMissing();
    } else {
      dispatch(push(getToUrl(`/checkout/${INFORMATION_PAGE}`, 'U39')));
    }
  };

  const taxes = useMemo(
    () =>
      (energySurcharge || 0) +
      (tax || 0) +
      (initialPaySurchargeAmt || 0) +
      (otherTaxesFees || 0) +
      (deposit || 0),
    [energySurcharge, tax, initialPaySurchargeAmt, otherTaxesFees, deposit],
  );

  // todo :: leverage this currency formatter everywhere - bring this into some Utils file
  total = formatCurrency(total || 0);
  recurringCharge = formatCurrency(recurringCharge || 0);
  const promotionDiscount = formatCurrency(discount || 0);

  const deliveryFeeInfo = injectVariables(
    '{deliveryFeeInfo}',
    getRegionalBrandName(null, 15),
    isAcquisition ? acquisitionVariables : costcoVariables,
    'C29',
  );

  useEffect(() => {
    setIsCostco(window.isCostcoWater);
  }, []);

  return (
    <div className={styles.summaryContainer}>
      <div
        className={styles.subTotal}
        role="status"
        aria-atomic="true"
        aria-live="polite"
      >
        <Span>CART SUBTOTAL:</Span>
        <span>{formatCurrency(subtotal)}</span>
      </div>
      <ButtonBase
        className={classnames(styles.taxes, isShowTaxes && styles.expanded)}
        type="button"
        style={{ width: '100%' }}
        onClick={toggleTaxes}
        aria-expanded={isShowTaxes}
        aria-controls="taxFeeSection"
      >
        <div className={styles.title}>
          {isShowTaxes ? (
            <KeyboardArrowDown className={styles.icon} />
          ) : (
            <KeyboardArrowRight className={styles.icon} />
          )}
          <Span>
            {`${
              isEstimatedDeliveryFee ? 'Est. ' : ''
            }TAXES, FEES & BOTTLE DEPOSITS:`}
          </Span>
        </div>
        {!isShowTaxes && (
          <Loader loading={isSummaryLoading} className={styles.loaderTaxes}>
            <span>{formatCurrency(taxes)}</span>
          </Loader>
        )}
      </ButtonBase>
      <Collapse in={isShowTaxes} id="taxFeeSection">
        <div className={styles.taxesExd}>
          <div>
            <Button
              text={`${isEstimatedDeliveryFee ? 'Est. ' : ''}Delivery Fee`}
              link
              onClick={toggleSurcharge}
              ariaLabel="display modal"
            />
            <span>{formatCurrency(energySurcharge)}</span>
          </div>
          {isCanadianBrand() ? (
            <>
              <div>
                <Button
                  text="Goods and Services Tax"
                  link
                  onClick={toggleGST}
                  ariaLabel="display modal"
                />
                <span>{formatCurrency(gstTax)}</span>
              </div>
              <div>
                <Button
                  text="Harmonized Sales Tax"
                  link
                  onClick={toggleHST}
                  ariaLabel="display modal"
                />
                <span>{formatCurrency(hstTax)}</span>
              </div>
              <div>
                <Button
                  text="Quebec Sales Tax"
                  link
                  onClick={toggleQST}
                  ariaLabel="display modal"
                />
                <span>{formatCurrency(qstTax)}</span>
              </div>
              <div>
                <Button
                  text="Provincial Sales Tax"
                  link
                  onClick={togglePST}
                  ariaLabel="display modal"
                />
                <span>{formatCurrency(pstTax)}</span>
              </div>
              <div>
                <Button
                  text="Container Recycling Fee"
                  link
                  onClick={toggleCRF}
                  ariaLabel="display modal"
                />
                <span>{formatCurrency(containerRecyclingFee)}</span>
              </div>
              <div>
                <Button
                  text="Container Deposit Fee"
                  link
                  onClick={toggleCDF}
                  ariaLabel="display modal"
                />
                <span>{formatCurrency(containerDeposit)}</span>
              </div>
            </>
          ) : (
            ''
          )}
          {!isCanadianBrand() ? (
            <div>
              <Button
                text="Sales tax"
                link
                onClick={toggleTax}
                ariaLabel="display modal"
              />
              <span>{formatCurrency(tax)}</span>
            </div>
          ) : (
            ''
          )}
          {!isCostco && (
            <div>
              <Button
                text="Credit Card Surcharge"
                link
                onClick={togglePaySurcharge}
                ariaLabel="display modal"
              />
              <span>{formatCurrency(initialPaySurchargeAmt)}</span>
            </div>
          )}
          <div>
            <Button
              text="Other taxes & fees"
              link
              onClick={toggleTax}
              ariaLabel="display modal"
            />
            <span>{formatCurrency(otherTaxesFees)}</span>
          </div>
          <div>
            <Button
              text="Refundable Bottle Deposit"
              link
              onClick={toggleDeposit}
              ariaLabel="display modal"
            />
            <span>{formatCurrency(deposit)}</span>
          </div>
        </div>
        <div className={styles.taxTotal}>
          <span>{formatCurrency(taxes)}</span>
        </div>
      </Collapse>
      {promotionId && discount > 0 && (
        <Fragment>
          <ButtonBase
            className={classnames(
              styles.taxes,
              isShowPromotion && styles.expanded,
            )}
            style={{ width: '100%' }}
            onClick={toggleShowPromotion}
          >
            <div>
              {isShowPromotion ? (
                <KeyboardArrowDown className={styles.icon} />
              ) : (
                <KeyboardArrowRight className={styles.icon} />
              )}
              <Span>PROMOTION APPLIED:</Span>
            </div>
            {!isShowPromotion && <span>-{promotionDiscount}</span>}
          </ButtonBase>
          <Collapse in={isShowPromotion}>
            <div className={styles.taxesExd}>
              <div className={styles.promotion}>
                <Span className={styles.title}>
                  {`${promotionDiscount} off your first invoice`}
                </Span>
                <span className={styles.discount}>-{promotionDiscount}</span>
              </div>
            </div>
          </Collapse>
          {isShowPromotion && (
            <div className={styles.taxTotal}>
              <span>-{promotionDiscount}</span>
            </div>
          )}
        </Fragment>
      )}
      <div
        className={styles.estimateBlock}
        role="status"
        aria-atomic="true"
        aria-live="polite"
      >
        <Span>ESTIMATED INITIAL INVOICE:</Span>
        <Loader
          loading={isSummaryLoading}
          className={styles.loaderInitialPrice}
        >
          <span className={styles.price}>{total}</span>
        </Loader>
        {isShowCheckoutButton && (
          <PromotionValidation
            buttonLabel="Checkout"
            promotionApplied={promotionId}
            onNext={goToCheckout}
            step={3}
            discount={discount}
            cartItems={cartItems}
            reviewCart={false}
            fullWidth
          />
        )}
      </div>
      <div className={styles.estimate}>
        <H2 className={styles.title}>
          What will my estimated recurring invoice be?
        </H2>
        <div>
          <p>
            <Typography variant="caption" className={styles.caption}>
              {translateData(
                'You are signing up for a recurring subscription that will be delivered every 2 or 4 weeks depending on availability in your area. Your recurring invoice may include multiple deliveries, exchange of bottle deposits and other fees.',
              )}
              <br />
              <br />
              {translateData(
                'The estimated recurring invoice below is an estimate that assumes you will receive the quantities in your cart once per 28 day billing period.',
              )}
            </Typography>
          </p>
        </div>

        <Collapse
          in={isShowDetails}
          className={styles.caption}
          id="detailsSection"
        >
          <div className={styles.estimateDetails}>
            <div id="cartTotal" aria-label={translateData('Cart Total')}>
              <Span>Cart Total</Span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div>
              <Button
                text={`${isEstimatedDeliveryFee ? 'Est. ' : ''}Delivery Fee`}
                link
                ariaLabel="display modal"
                onClick={toggleSurcharge}
              />
              <span>{formatCurrency(energySurcharge)}</span>
            </div>
            {!isCanadianBrand() ? (
              <div>
                <Button
                  text="Sales tax"
                  link
                  ariaLabel="display modal"
                  onClick={toggleTax}
                />
                <span>{formatCurrency(tax)}</span>
              </div>
            ) : (
              ''
            )}
            {!isCostco && (
              <div>
                <Button
                  text="Credit Card Surcharge"
                  link
                  onClick={togglePaySurcharge}
                  ariaLabel="display modal"
                />
                <span>{formatCurrency(recurringPaySurchargeAmt)}</span>
              </div>
            )}
            <div>
              <Button
                text="Other taxes & fees"
                link
                ariaLabel="display modal"
                onClick={toggleTax}
              />
              <span>{formatCurrency(otherTaxesFees)}</span>
            </div>
            <div>
              <Button
                text="Refundable Bottle Deposit"
                link
                onClick={toggleDeposit}
                aria-label="display refundable bottle deposit"
              />
              <span>{formatCurrency(0)}</span>
            </div>
            {isCanadianBrand() ? (
              <>
                <div>
                  <Button
                    text="Goods and Services Tax"
                    link
                    onClick={toggleGST}
                    ariaLabel="display modal"
                  />
                  <span>{formatCurrency(gstTax)}</span>
                </div>
                <div>
                  <Button
                    text="Harmonized Sales Tax"
                    link
                    onClick={toggleHST}
                    ariaLabel="display modal"
                  />
                  <span>{formatCurrency(hstTax)}</span>
                </div>
                <div>
                  <Button
                    text="Quebec Sales Tax"
                    link
                    onClick={toggleQST}
                    ariaLabel="display modal"
                  />
                  <span>{formatCurrency(qstTax)}</span>
                </div>
                <div>
                  <Button
                    text="Provincial Sales Tax"
                    link
                    onClick={togglePST}
                    ariaLabel="display modal"
                  />
                  <span>{formatCurrency(pstTax)}</span>
                </div>
                <div>
                  <Button
                    text="Container Recycling Fee"
                    link
                    onClick={toggleCRF}
                    ariaLabel="display modal"
                  />
                  <span>{formatCurrency(containerRecyclingFee)}</span>
                </div>
                <div>
                  <Button
                    text="Container Deposit Fee"
                    link
                    onClick={toggleCDF}
                    ariaLabel="display modal"
                  />
                  <span>{formatCurrency(containerDeposit)}</span>
                </div>
              </>
            ) : (
              ''
            )}
          </div>
        </Collapse>
        {!isShowDetails && (
          <div className={styles.totalPrice}>
            <span>{recurringCharge}</span>
            <Button
              text="See Details"
              link
              onClick={toggleDetails}
              type="button"
              aria-expanded="false"
              aria-controls="detailsSection"
              data-tracking="See Details"
            />
          </div>
        )}
        {isShowDetails && (
          <div className={styles.totalPrice}>
            <span>{recurringCharge}</span>
            <Button
              text="Hide details"
              link
              onClick={toggleDetails}
              type="button"
              aria-expanded="true"
              aria-controls="detailsSection"
              data-tracking="Hide details"
            />
          </div>
        )}
      </div>
      <div className={styles.viewSampleInvoice}>
        <DescriptionIcon className={styles.icon} />
        <Button
          text="View a sample invoice"
          link
          onClick={toggleViewSampleInvoice}
          type="button"
        />
      </div>
      <HowToReadYourBillDialog
        open={isViewSampleInvoice}
        isAcquisition={isAcquisition}
        onClose={toggleViewSampleInvoice}
      />
      <Dialog
        size="small"
        onClose={toggleSurcharge}
        open={modalStates.surcharge}
        fullScreen={isMobile}
        aria-labelledby="Delivery Fee"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Delivery Fee" className={styles.title}>
            {`${isEstimatedDeliveryFee ? 'Estimated ' : ''}Delivery Fee`}
          </H2>
          <div className={styles.text}>{deliveryFeeInfo}</div>
        </div>
      </Dialog>
      <Dialog
        size="small"
        onClose={toggleDeposit}
        open={modalStates.deposit}
        fullScreen={isMobile}
        aria-labelledby="Refundable Bottle Deposit"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Refundable Bottle Deposit" className={styles.title}>
            Refundable Bottle Deposit
          </H2>
          <Div className={styles.text}>
            {
              "On your invoice, you'll be charged a refundable bottle deposit for each 11.3L or 18.9L  bottle included in your delivery. These fees will be reconciled if you close your account."
            }
          </Div>
        </div>
      </Dialog>
      <Dialog
        size="small"
        onClose={togglePST}
        open={modalStates.pst}
        fullScreen={isMobile}
        aria-labelledby="Provincial Sales Tax"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Provincial Sales Tax" className={styles.title}>
            Provincial Sales Tax
          </H2>
          <Div className={styles.text}>
            {'Sales tax collected by some provinces.'}
          </Div>
        </div>
      </Dialog>
      <Dialog
        size="small"
        onClose={toggleGST}
        open={modalStates.gst}
        fullScreen={isMobile}
        aria-labelledby="Goods and Services Tax"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Goods and Services Tax" className={styles.title}>
            Goods and Services Tax
          </H2>
          <Div className={styles.text}>
            {
              'The Goods and Services Tax (GST) is a tax that applies to most goods and services made or rendered in Canada.'
            }
          </Div>
        </div>
      </Dialog>
      <Dialog
        size="small"
        onClose={toggleHST}
        open={modalStates.hst}
        fullScreen={isMobile}
        aria-labelledby="Harmonized Sales Tax"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Harmonized Sales Tax" className={styles.title}>
            Harmonized Sales Tax
          </H2>
          <Div className={styles.text}>
            {
              'Some provinces have harmonized their Provincial Sales Tax (PST) with the Goods and Services Tax (GST), applied to the same base of goods and services.'
            }
          </Div>
        </div>
      </Dialog>
      <Dialog
        size="small"
        onClose={toggleQST}
        open={modalStates.qst}
        fullScreen={isMobile}
        aria-labelledby="Quebec Sales Tax"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Quebec Sales Tax" className={styles.title}>
            Quebec Sales Tax
          </H2>
          <Div className={styles.text}>
            {
              'The Quebec Sales Tax (QST) is the tax applied to goods or services provided in Quebec.'
            }
          </Div>
        </div>
      </Dialog>
      <Dialog
        size="small"
        onClose={togglePST}
        open={modalStates.pst}
        fullScreen={isMobile}
        aria-labelledby="Provincial Sales Tax"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Provincial Sales Tax" className={styles.title}>
            Provincial Sales Tax
          </H2>
          <Div className={styles.text}>
            {
              'Provincial sales tax (PST) is a retail sales tax that applies when taxable goods or services are purchased, acquired, or brought into the province for use in the province. PST rates vary by province.'
            }
          </Div>
        </div>
      </Dialog>
      <Dialog
        size="small"
        onClose={toggleCRF}
        open={modalStates.crf}
        fullScreen={isMobile}
        aria-labelledby="Container Recycling Fee"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Container Recycling Fee" className={styles.title}>
            Container Recycling Fee
          </H2>
          <Div className={styles.text}>
            {
              'The Container Recycling Fee (CRF) is charged to cover the net cost of recycling a beverage container. The CRF is non-refundable and varies by province for each beverage container category.'
            }
          </Div>
        </div>
      </Dialog>
      <Dialog
        size="small"
        onClose={toggleCDF}
        open={modalStates.cdf}
        fullScreen={isMobile}
        aria-labelledby="Container Deposit Fee"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Container Deposit Fee" className={styles.title}>
            Container Deposit Fee
          </H2>
          <Div className={styles.text}>
            {
              'The Container Deposit Fee is charged when a product is purchased and is refundable in full when the empty container is brought back for recycling. The Container Deposit fee varies by Province for each beverage container category.'
            }
          </Div>
        </div>
      </Dialog>
      <Dialog
        size="small"
        onClose={togglePaySurcharge}
        open={modalStates.paySurcharge}
        fullScreen={isMobile}
        aria-labelledby="Credit Card Surcharge"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Credit Card Surcharge" className={styles.title}>
            Credit Card Surcharge
          </H2>
          <Div className={styles.text}>CC fee/surcharge disclaimer</Div>
        </div>
      </Dialog>
      <Dialog
        size="small"
        onClose={toggleTax}
        open={modalStates.taxes}
        fullScreen={isMobile}
        aria-labelledby="Sales and other tax"
      >
        <div className={styles.modal}>
          <img src={InfoIcon} alt="" />
          <H2 id="Sales and other tax" className={styles.title}>
            Sales and other tax
          </H2>
          <Div className={styles.text}>
            {
              "If your order isn't tax exempt, taxes are estimated. Final applicable sales tax will be reflected on each invoice. You will be charged taxes on the products you receive in each delivery."
            }
          </Div>
        </div>
      </Dialog>

      {isCartDispenserMissing && (
        <EquipmentNotification
          products={equipmentItems}
          dispenserMissing={dispenserMissing}
        />
      )}
    </div>
  );
};

PriceSummary.propTypes = {
  total: PropTypes.number,
  subtotal: PropTypes.number,
  energySurcharge: PropTypes.number,
  tax: PropTypes.number,
  otherTaxesFees: PropTypes.number,
  deposit: PropTypes.number,
  recurringCharge: PropTypes.number,
  isShowCheckoutButton: PropTypes.bool,
  isSummaryLoading: PropTypes.bool,
  promotionId: PropTypes.number,
  discount: PropTypes.number,
  cartItems: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  products: selectProductsData(),
  costcoVariables: selectCostcoVariables(),
  acquisitionVariables: selectAcquisitionVariables(),
  isSummaryLoading: selectSummaryLoading(),
});

const mapDispatchToProps = {
  loadMerchandizedProducts,
};

export default connect(mapStateToProps, mapDispatchToProps)(PriceSummary);
